import { Box, FormControlLabel, Grid, Radio, RadioGroup, Switch } from '@mui/material'
import React, { useMemo } from 'react'
import { formatDate, convertToLabelValue } from '../../../app/utils'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useGetAllTransportModesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useGetCarriersQuery } from '../../../app/redux-fetch/apiOrders'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormDatePickerInput from '../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import { InputHeader } from '../../../components/Headers/styles'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { useOrderFormContext } from '../context/OrderFormContext'
import messages from './messages'
import { IShipmentFormInput } from './schema'

const ROAD_TRANSPORT_MODE_ID = 3

interface TransitDetailsFormProps {
  namePrefix: string
  consignmentIndex: number
  disabled?: boolean
}

const TransitDetailsForm: React.FC<TransitDetailsFormProps> = ({
  namePrefix,
  disabled = false,
  consignmentIndex,
}) => {
  const { control, setValue, watch, trigger } = useFormContext()
  const { formatMessage } = useIntl()
  const { customerId } = useLoggedInUser()
  const carrierOption = useWatch({
    name: `${namePrefix}.carrierOption`,
  })

  const tenantId = watch('tenantId')
  const transportModeId = watch(`${namePrefix}.modeOfTransit`)

  const { data: carries = [] } = useGetCarriersQuery(
    { tenantId: tenantId, customerId: customerId },
    {
      skip: !tenantId,
    }
  )

  const carrierOptions = useMemo(() => {
    return convertToLabelValue(
      transportModeId || transportModeId !== 0
        ? carries.filter((carrier) => carrier.transportModeId === transportModeId)
        : carries
    )
  }, [carries, transportModeId])

  const { data: transportModes = [] } = useGetAllTransportModesQuery()

  const transportModeOptions = useMemo(() => {
    const result = transportModes.map((mode) => (mode.id === 0 ? { ...mode, id: -1 } : mode))
    return convertToLabelValue(result)
  }, [transportModes])

  const { variant } = useOrderFormContext()

  const modeOfTransit = watch(`${namePrefix}.modeOfTransit`)
  const carrierOptionValue = watch(`${namePrefix}.carrierOption`)
  const carrierId = watch(`${namePrefix}.carrierId`)
  const transitIdentifier = watch(`${namePrefix}.transitIdentifier`)
  const crossBorder = watch(`${namePrefix}.crossBorder`)
  const fulfilmentRequired = watch(`${namePrefix}.fulfilmentRequired`)
  const scheduledDate = watch(`${namePrefix}.scheduledDate`)

  const isRoadMode = modeOfTransit === ROAD_TRANSPORT_MODE_ID
  const isPreBookedTransit = carrierOptionValue === 'preBookedTransit'
  const shouldShowTransitIdentifier = (!isRoadMode && carrierOptionValue === 'preBookedTransit')
  const shouldShowScheduledDate = (!isRoadMode && isPreBookedTransit)

  const getModeOfTransitLabel = () => {
    const mode = transportModes.find((m: any) => m.id === modeOfTransit)
    return mode ? mode.name : 'N/A'
  }

  const getCarrierLabel = () => {
    const carrier = carries.find((c: any) => c.id === carrierId)
    return carrier ? carrier.name : 'N/A'
  }

  const getCarrierOptionLabel = () => {
    if (carrierOptionValue === 'noPreference') return formatMessage(messages.noPreference)
    if (carrierOptionValue === 'preferredCarrier') return formatMessage(messages.preferredCarrier)
    if (carrierOptionValue === 'preBookedTransit') return formatMessage(messages.preBookedTransit)
    return 'N/A'
  }

  if (variant === 'summary') {
    return (
      <DefaultSpacingGrid container>
        <Grid item xs={12} md={6}>
          <InfoDisplayItem
            label={formatMessage(messages.modeOfTransit)}
            value={getModeOfTransitLabel()}
            isSmallScreen={false}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ ml: '-36px' }}>
          <InfoDisplayItem
            label={isRoadMode ? formatMessage(messages.fulfilmentRequired) : formatMessage(messages.carrierPreference)}
            value={isRoadMode ? (fulfilmentRequired ? 'Yes' : 'No') : getCarrierOptionLabel()}
            isSmallScreen={false}
          />
        </Grid>
        {(!isRoadMode && (carrierOptionValue === 'preferredCarrier' ||
          carrierOptionValue === 'preBookedTransit')) ? (
          <Grid item xs={12} md={6}>
            <InfoDisplayItem
              label={formatMessage(messages.carrier)}
              value={getCarrierLabel()}
              isSmallScreen={false}
            />
          </Grid>
        ) : null}
        {shouldShowTransitIdentifier && (
          <Grid item xs={12} md={3} sx={{ ml: '-36px' }}>
            <InfoDisplayItem
              label={formatMessage(messages.transitIdentifier)}
              value={transitIdentifier || 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
        )}
        {shouldShowScheduledDate && (
          <Grid item xs={12} md={3}>
            <InfoDisplayItem
              label={formatMessage(messages.requestedDate)}
              value={scheduledDate ? formatDate(scheduledDate) : 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
        )}
        <Grid item xs={12} md={2} sx={{ ml: carrierOptionValue === 'preferredCarrier' ? '-36px' : '0px' }}>
          <InfoDisplayItem
            label={formatMessage(messages.crossBorder)}
            value={crossBorder ? 'Yes' : 'No'}
            isSmallScreen={false}
          />
        </Grid>
      </DefaultSpacingGrid>
    )
  }
  return (
    <DefaultSpacingGrid container>
      <Grid item xs={12} md={4}>
        <FormSelect
          name={`${namePrefix}.modeOfTransit`}
          label={formatMessage(messages.modeOfTransit)}
          options={transportModeOptions}
          onChange={() => {
            setValue(`${namePrefix}.carrierId`, undefined)
            setValue(`${namePrefix}.carrierOption`, 'noPreference')
            setValue(`${namePrefix}.fulfilmentRequired`, undefined)
            setValue(`${namePrefix}.scheduledDate`, null)
            setValue(`${namePrefix}.transitIdentifier`, '')
          }}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      {isRoadMode ? (
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
            }}
          >
            <Controller
              name={`${namePrefix}.fulfilmentRequired` as keyof IShipmentFormInput}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={Boolean(field.value)}
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e)
                      }}
                    />
                  }
                  label={formatMessage(messages.fulfilmentRequired)}
                  sx={{ marginTop: '8px' }}
                />
              )}
            />
          </Box>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} md={8} container justifyContent='center' alignItems='center'>
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <InputHeader gutterBottom>{formatMessage(messages.carrierPreference)}</InputHeader>
              </Box>
              <Box>
                <Controller
                  name={`${namePrefix}.carrierOption` as keyof IShipmentFormInput}
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      row
                      sx={{ margin: 'auto' }}
                      onChange={(e) => {
                        field.onChange(e)
                        const value = e.target.value
                        if (value === 'noPreference') {
                          setValue(`${namePrefix}.carrierId`, undefined)
                          setValue(`${namePrefix}.transitIdentifier`, '')
                          setValue(`${namePrefix}.scheduledDate`, null)
                        } else if (value === 'preferredCarrier') {
                          setValue(`${namePrefix}.transitIdentifier`, '')
                          setValue(`${namePrefix}.scheduledDate`, null)
                        }
                      }}
                    >
                      <FormControlLabel
                        value='noPreference'
                        control={<Radio disabled={disabled} />}
                        label={formatMessage(messages.noPreference)}
                      />
                      <FormControlLabel
                        value='preferredCarrier'
                        control={<Radio disabled={disabled} />}
                        label={formatMessage(messages.preferredCarrier)}
                      />
                      <FormControlLabel
                        value='preBookedTransit'
                        control={<Radio disabled={disabled} />}
                        label={formatMessage(messages.preBookedTransit)}
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
            </Box>
          </Grid>
        </>
      )}
      {(!isRoadMode && (carrierOption === 'preferredCarrier' || carrierOption === 'preBookedTransit')) ? (
        <>
          <Grid item xs={12} md={4}>
            <FormSelect
              name={`${namePrefix}.carrierId`}
              options={carrierOptions}
              label={formatMessage(messages.carrier)}
              placeholder={formatMessage(messages.enterCarrier)}
              fullWidth
              required={true}
              disabled={disabled}
            />
          </Grid>
          {shouldShowScheduledDate && (
            <Grid item xs={12} md={4}>
              <FormDatePickerInput
                name={`${namePrefix}.scheduledDate`}
                label={formatMessage(messages.scheduledDateLabel)}
                required={true}
                disabled={disabled}
                format='YYYY-MM-DD HH:mm'
              />
            </Grid>
          )}
          {shouldShowTransitIdentifier && (
            <Grid item xs={12} md={4}>
              <FormInput
                name={`${namePrefix}.transitIdentifier`}
                label={formatMessage(messages.transitIdentifier)}
                placeholder={formatMessage(messages.enterTransitIdentifier)}
                fullWidth
                required={true}
                maxLength={20}
                disabled={disabled}
              />
            </Grid>
          )}
        </>
      ) : null}
      <Grid item xs={12} md={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
          }}
        >
          <Controller
            name={`${namePrefix}.crossBorder` as keyof IShipmentFormInput}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={Boolean(field.value)}
                    disabled={disabled}
                    onChange={(e) => {
                      field.onChange(e)
                      trigger(`bookings.${consignmentIndex}.clearingRequired`)
                    }}
                  />
                }
                label={formatMessage(messages.crossBorder)}
                sx={{ marginTop: 2 }}
              />
            )}
          />
        </Box>
      </Grid>
    </DefaultSpacingGrid>
  )
}

export default TransitDetailsForm
